import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDate } from "@/lib/format";
import { PageHeader, Card, Loading, Pill, SeverityTag, EmptyState } from "@/components/kit";
import TransactionDetail from "@/components/TransactionDetail";
import { AlertCircle } from "lucide-react";

export default function Outstanding() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("oldest");
  const [detail, setDetail] = useState(null);

  const load = () => api.ownerOutstanding().then(setData).catch((e) => { toast.error(e.message); setData({ count: 0, total_outstanding: 0, items: [] }); });
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const set = new Set((data?.items || []).map((i) => i.category).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [data]);

  const rows = useMemo(() => {
    let r = (data?.items || []).filter((i) => category === "all" || i.category === category);
    if (sort === "oldest") r = [...r].sort((a, b) => a.transaction_at.localeCompare(b.transaction_at));
    else if (sort === "newest") r = [...r].sort((a, b) => b.transaction_at.localeCompare(a.transaction_at));
    else if (sort === "largest") r = [...r].sort((a, b) => b.remaining - a.remaining);
    return r;
  }, [data, category, sort]);

  return (
    <div>
      <PageHeader eyebrow={user.role === "owner" ? "Owner" : "Admin"} title="Sisa Tagihan"
        subtitle="Transaksi DP / belum lunas. Pelunasan menambah pembayaran ke tagihan yang sama — revenue tidak dihitung ganda." />

      {data === null ? <Loading /> : (
        <>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Card className="flex items-center gap-4" data-testid="outstanding-widget">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1a0d0d]"><AlertCircle className="h-6 w-6 text-[#FF6B6B]" /></div>
              <div>
                <div className="hsc-label">Outstanding</div>
                <div className="font-display text-2xl text-white">{data.count} transaksi</div>
              </div>
            </Card>
            <Card>
              <div className="hsc-label">Total Sisa Tagihan</div>
              <div className="mt-2 font-display text-2xl text-[#FF8080]">{formatIDR(data.total_outstanding)}</div>
            </Card>
          </div>

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => <Pill key={c} active={category === c} onClick={() => setCategory(c)}>{c === "all" ? "Semua" : c}</Pill>)}
            </div>
            <select className="hsc-input w-auto" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="outstanding-sort">
              <option value="oldest">Terlama</option>
              <option value="newest">Terbaru</option>
              <option value="largest">Sisa Terbesar</option>
            </select>
          </div>

          {!rows.length ? (
            <EmptyState icon={AlertCircle} title="Tidak ada tagihan tertunggak" subtitle="Semua transaksi sudah lunas." />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-[#1F1F22]">
              <table className="w-full text-sm">
                <thead className="bg-[#0A0A0C] text-left text-[11px] uppercase tracking-widest text-[#6E6E73]">
                  <tr>
                    <th className="px-4 py-3">Customer</th>
                    <th className="hidden px-4 py-3 md:table-cell">Tanggal</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 text-right">Dibayar</th>
                    <th className="px-4 py-3 text-right">Sisa</th>
                    <th className="px-4 py-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.bill_id} className="cursor-pointer border-t border-[#141416] hover:bg-[#0A0A0C]" data-testid="outstanding-row"
                      onClick={() => setDetail({ id: r.first_txn_id, bill_id: r.bill_id, member_id: r.member_id, member_name: r.member_name, category: r.category, txn_type: r.txn_type, amount: r.total, transaction_at: r.transaction_at, class_name: r.class_name, court: r.court })}>
                      <td className="px-4 py-3"><div className="text-white">{r.member_name}</div><div className="text-[11px] text-[#6E6E73]">{r.category}</div></td>
                      <td className="hidden px-4 py-3 text-[#A1A1A6] md:table-cell">{formatDate(r.transaction_at)}</td>
                      <td className="px-4 py-3 text-right text-[#A1A1A6]">{formatIDR(r.total)}</td>
                      <td className="px-4 py-3 text-right text-[#7CD992]">{formatIDR(r.paid)}</td>
                      <td className="px-4 py-3 text-right font-medium text-[#FF8080]">{formatIDR(r.remaining)}</td>
                      <td className="px-4 py-3 text-center"><SeverityTag value={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {detail && (
        <TransactionDetail txn={detail} canEdit canDelete={user.role === "owner"}
          onClose={() => setDetail(null)} onChanged={load} />
      )}
    </div>
  );
}
